import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid';
import { useDispatch, useSelector } from '../../../../redux/store';
import { fCurrency, fNumber } from '../../../../utils/formatNumber';
import { InvoiceItemParsedField } from '../../../../utils/globalEnums';
import Iconify from '../../../../reusable-components/iconify';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import {
  patchInvoiceItems,
  deleteGlItems,
  getInvoiceBreakdown,
} from '../../../../redux/slices/invoicing';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import usePrompt from '../../../../reusable-components/prompt/usePrompt';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { HOST_API_URL } from '../../../../config-global';
import CategorySelector from './reusable/CategorySelector';
import { bobAIIcon } from '../../../../reusable-components/bobAI/bobAI';
import { useIsInvoiceEditable } from './reusable/hooks/useInvoiceLocks';

OrderInformation.propTypes = {
  highlightMissingGls: PropTypes.bool,
  expandBottom: PropTypes.bool,
  setUnsavedData: PropTypes.func,
  unsavedData: PropTypes.object,
  highlightItem: PropTypes.object,
  invoiceDataLoaded: PropTypes.bool,
  addItemsToChat: PropTypes.bool,
  setAddItemsToChat: PropTypes.func,
  selectedChatItemIds: PropTypes.array,
  setSelectedChatItemIds: PropTypes.func,
};
export default function OrderInformation({
  highlightMissingGls,
  expandBottom,
  setUnsavedData,
  unsavedData,
  highlightItem,
  invoiceDataLoaded,
  addItemsToChat,
  setAddItemsToChat,
  selectedChatItemIds,
  setSelectedChatItemIds,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const {
    data: { invoiceData, invoiceItemData, invoiceGLItems },
  } = useSelector((state) => state.invoicing);

  const { invoice, maximumItemDiscrepancy } = invoiceItemData || {};
  const { categories: cats, manualCategories, subcategories: subcats, manualSubcategories } = invoiceData;
  const isLoading = useSelector((state) => state.invoicing.isLoading);
  const [rows, setRows] = useState([]);
  const [confirmUnlockItems, setConfirmUnlockItems] = useState(false);
  const categories =
    invoiceItemData?.invoice?.sourceMethod === null ? cats : /*sourceMethod === "Manual"*/ manualCategories || [];
  const subcategories =
    invoiceItemData?.invoice?.sourceMethod === null ? subcats : /*sourceMethod === "Manual"*/ manualSubcategories || [];

  const { isEditable: hasEditAccess } = useIsInvoiceEditable();
 const editable = !invoiceItemData?.invoice?.multiFacility && invoiceGLItems?.length === 0 && hasEditAccess;

  const apiRef = useGridApiRef();

  const updateUnsavedData = (rowId, updatedRow) => {
    setRows((prevRows) => {
      const exists = prevRows.some((r) => r.id === rowId);

      if (!exists) {
        return [...prevRows, updatedRow];
      }

      return prevRows.map((r) => (r.id === rowId ? updatedRow : r));
    });

    setUnsavedData((prev) => ({
      ...prev,
      [rowId]: updatedRow,
    }));
    apiRef.current.updateRows([updatedRow]);
  };

  const renderEditableNumber = (field, row, currency = false) =>
    editable ? (
      <TextField
        type="number"
        disabled={!hasEditAccess}
        size="small"
        value={row[field]}
        autoComplete="off"
        onChange={(e) => {
          const updatedRow = { ...row, [field]: Number(e.target.value) };
          updateUnsavedData(row.id, updatedRow);
        }}
        InputProps={
          currency && {
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#404042' }}>$</Typography>
              </InputAdornment>
            ),
          }
        }
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '0.8rem',
            fontWeight: 600,
          },
        }}
      />
    ) : currency ? (
      fCurrency(row[field])
    ) : (
      row[field]
    );

  const renderEditableString = (field, row) =>
    editable ? (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', height: '100%' }}>
        <TextField
          type={String}
          disabled={!hasEditAccess}
          autoComplete="off"
          size="small"
          value={row[field]}
          onChange={(e) => {
            const updatedRow = { ...row, [field]: e.target.value };
            updateUnsavedData(row.id, updatedRow);
          }}
          fullWidth
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.8rem',
              fontWeight: 600,
            },
          }}
        />
      </Box>
    ) : (
      row[field]
    );

  const renderParsedField = (isParsed, content, showTooltip = false) => (
    <Box sx={{ display: 'flex', flex: 1 }} alignItems="center" gap={0.5}>
      {isParsed && bobAIIcon()}
      {showTooltip ? (
        <Tooltip title={typeof content === 'string' ? content : ''} placement="bottom" arrow>
          <Box
            sx={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
            }}
          >
            {' '}
            {content}
          </Box>
        </Tooltip>
      ) : (
        content
      )}
    </Box>
  );
  const handleGlCategoryChange = (newValue, item, index, nonDependantGls) => {
    const updatedRow = {
      ...item,
      categoryId: newValue?.categoryId ?? 0,
      category: newValue?.category ?? '',
      ...(!nonDependantGls ? { subcategoryId: '', subcategory: '', glCode: '' } : {}),
    };
    updateUnsavedData(item.id, updatedRow);
  };
  const handleGlSubcategoryChange = (newValue, item, index, nonDependantGls) => {
    if (newValue && newValue.value === 'add-gl-code') {
      navigate('/dashboard/invoicing/vendors?tab=1');
      return;
    }
    const { category } =
      categories
        ?.filter((category) => category?.facilityId === item?.facilityId)
        ?.find((item) => item.categoryId === newValue.categoryId) || {};
    const updatedRow = {
      ...item,
      subcategoryId: newValue.subcategoryId,
      subcategory: newValue.subcategory,
      glCode: newValue.glCode,
      ...(!nonDependantGls ? { categoryId: newValue.categoryId, category: category ?? '' } : {}),
    };

    updateUnsavedData(item.id, updatedRow);
  };


  const getCategoryName = (row) => {
    const categoryObj = categories
      ?.filter((cat) => cat.facilityId === invoice?.facilityId)
      ?.find((x) => x.categoryId === row?.categoryId);

    return categoryObj
      ? `${categoryObj.category}${categoryObj.description ? ` (${categoryObj.description})` : ''}`
      : null;
  }

  const renderCategory = (params) => {
    const { row } = params;
    if (editable) {
      return (
        <CategorySelector
          isCategory
          currentCategory={row.categoryId}
          currentSubcategory={row.subcategoryId}
          handleChange={handleGlCategoryChange}
          variant="outlined"
          item={row}
          disabled={!invoiceDataLoaded}
          facilityId={invoice?.facilityId}
          textFieldProps={{
            InputProps: {
              endAdornment: <>{!invoiceDataLoaded ? <CircularProgress color="inherit" size={16} /> : null}</>,
              style: {
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: 0,
                paddingRight: '25px',
              },
            },
          }}
        />
      );
    }
    return getCategoryName(row);
  };

  const getSubcategoryName = (row) => {
    const subcategoryObj = subcategories
      ?.filter((sub) => sub.facilityId === invoice?.facilityId)
      ?.find((x) => x.subcategoryId === row.subcategoryId);

    return row
      ? `${subcategoryObj?.subcategory ?? ''} ${subcategoryObj?.glCode ? `(${subcategoryObj?.glCode})` : ''}`
      : null;
  }
  const renderSubcategory = (params) => {
    const { row } = params;

    if (editable) {
      return (
        <CategorySelector
          isCategory={false}
          currentCategory={row.categoryId}
          currentSubcategory={row.subcategoryId}
          handleChange={handleGlSubcategoryChange}
          disabled={!invoiceDataLoaded}
          textFieldProps={{
            InputProps: {
              endAdornment: <>{!invoiceDataLoaded ? <CircularProgress color="inherit" size={16} /> : null}</>,
              style: {
                fontSize: '0.8rem',
                fontWeight: 600,
                padding: 0,
                paddingRight: '25px',
              },
            },
          }}
          variant="outlined"
          item={row}
          facilityId={invoice?.facilityId}
        />
      );
    }

    return getSubcategoryName(row);
  };

  usePrompt(Object.values(unsavedData)?.length > 0);
  const invoiceItemTotal = {
    field: 'total',
    headerName: 'Invoice Total',
    width: 100,
    type: 'customMoney',
    valueGetter: (params) => fCurrency(params.row.quantity * params.row.price + params.row.taxAmount),
  };
  const categoryDropdown = {
    field: 'categoryId',
    headerName: 'Category',
    width: 140,
    maxHeight: 45,
    overflow: 'hidden',
    renderCell: (params) => {
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.Category) > 0;
      const content = renderCategory(params);
      return renderParsedField(isParsed, content);
    },
    type: 'customSelect',
    valueGetter: (params) => getCategoryName(params.row),
    getFilterValues: (row) => [getCategoryName(row)].filter(v => v),
  };
  const subCategoryDropdown = {
    field: 'subcategoryId',
    headerName: 'Subcategory',
    width: 240,
    renderCell: (params) => {
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.Subcategory) > 0;
      const content = renderSubcategory(params);
      return renderParsedField(isParsed, content);
    },
    type: 'customSelect',
    valueGetter: (params) => getSubcategoryName(params.row),
    getFilterValues: (row) => [getSubcategoryName(row)].filter(v => v),
  };
  const price = {
    field: 'price',
    headerName: 'Invoice Price',
    width: 100,
    renderCell: (params) => {
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.Price) > 0;
      const content = renderEditableNumber('price', params.row, true);
      return renderParsedField(isParsed, content);
    },
    valueGetter: (params) => params.row.price,
  };
  const manual = {
    field: 'manual',
    headerName: 'manual',
    valueGetter: (params) => params.row.manual ?? false,
  };
  const taxAmount = {
    field: 'taxAmount',
    headerName: 'Tax Amount',
    width: 100,
    renderCell: (params) => {
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.TaxAmount) > 0;
      const content = renderEditableNumber('taxAmount', params.row, true);
      return renderParsedField(isParsed, content);
    },
    valueGetter: (params) => params.row.taxAmount,
  };
  const description = {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 200,
    type: 'customSelect',

    renderCell: (params) => {
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.Description) > 0;
      const content = params.row.manual ? renderEditableString('description', params.row) : params.value;
      return renderParsedField(isParsed, content, !params.row.manual || !editable);
    },

    valueGetter: (params) => params.row.description,
  };
  const vendorItemId = {
    field: 'vendorItemId',
    headerName: 'SKU',
    width: 100,
    renderCell: (params) => {
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.VendorItemId) > 0;
      const content = params.row.manual ? renderEditableString('vendorItemId', params.row) : params.row.vendorItemId;
      return renderParsedField(isParsed, content);
    },
    valueGetter: (params) => params.row.vendorItemId,
    type: 'customSelect'
  };
  const expectedItemPrice = {
    field: 'expectedItemPrice',
    headerName: 'PO Price',
    width: 100,
    renderCell: (params) => {
      const overMax = Math.abs(params.row.price - params.row.expectedItemPrice) > maximumItemDiscrepancy;
      const notExact = params.row.price !== params.row.expectedItemPrice;
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.ExpectedItemPrice) > 0;
      const content = (
        <Tooltip
          title={
            overMax
              ? 'Our price does not match their price'
              : notExact
              ? `Price within acceptable range (${fCurrency(maximumItemDiscrepancy)})`
              : ''
          }
          placement="top"
          arrow
        >
          <Box color={overMax ? 'error.main' : notExact ? 'warning.main' : 'black'}>
            {notExact && <Iconify icon={'material-symbols:error-outline'} width={13} height={13} />}
            {fCurrency(params.row.expectedItemPrice)}
          </Box>
        </Tooltip>
      );
      return renderParsedField(isParsed, content);
    },
    valueGetter: (params) => {
      const overMax = Math.abs(params.row.price - params.row.expectedItemPrice) > maximumItemDiscrepancy;
      const notExact = params.row.price !== params.row.expectedItemPrice;
      return overMax
        ? `99999999${params.row.expectedItemPrice}`
        : notExact
        ? `88888888${params.row.expectedItemPrice}`
        : params.row.expectedItemPrice;
    },
  };
  const invoicedQtyWithUOM = {
    field: 'invoicedQtyWithUOM',
    headerName: 'Invoice/UOM',
    type: 'customSelect',
    getFilterValues: (row) => {
      const quantity = row.quantity || '';
      const uomType = row.uomType || '';
      return [`${quantity} ${uomType}`];
    },
    valueGetter: (params) => {
      const quantity = params.row.quantity || '';
      const uomType = params.row.uomType || '';
      return `${quantity} ${uomType}`;
    },
    renderCell: (params) => {
      const quantity = params.row.quantity || '';
      const uomType = params.row.uomType || '';
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.Quantity) > 0;
      const content = params.row.manual ? (
        renderEditableNumber('quantity', params.row, false)
      ) : (
        <Box
          component="div"
          sx={{
            fontSize: '0.8rem',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
          }}
        >
          {`${quantity} ${uomType}`}
        </Box>
      );
      return renderParsedField(isParsed, content);
    },
  };
  const poQtyWithUOM = {
    field: 'poQtyWithUOM',
    headerName: 'PO/UOM',
    type: 'customSelect',
    getFilterValues: (row) => {
      const poQuantity = row.expectedAmount;
      const uomType = row.poUOMType || '';
      return [`${poQuantity} ${uomType}`];
    },
    valueGetter: (params) => {
      const poQuantity = params.row.expectedAmount;
      const uomType = params.row.poUOMType || '';
      return `${poQuantity} ${uomType}`;
    },
    renderCell: (params) => {
      const moreSentThenOrdered = params.row.quantity > params.row.expectedAmount;
      const isParsed = (params.row.parsedFields & InvoiceItemParsedField.ExpectedAmount) > 0;
      const content = (
        <>
          <Tooltip
            title={moreSentThenOrdered ? `More sent then ordered (${params.row.quantity})` : ''}
            placement="top"
            arrow
          >
            <Box color={moreSentThenOrdered ? 'error.main' : 'black'}>
              {moreSentThenOrdered && <Iconify icon={'material-symbols:error-outline'} width={13} height={13} />}
              {fNumber(params.row.expectedAmount)}&nbsp;
            </Box>
          </Tooltip>{' '}
          {params.row.poUOMType}
        </>
      );
      return renderParsedField(isParsed, content);
    },
  };
  const actionButtons = (params) => {
    const { row } = params;
    return (
      <>
        {editable && (
          <IconButton
            color="error"
            actionButton="Delete"
            onClick={async () => {
              const rowToDelete = { ...row, deleteFlag: true, _action: 'delete' };
              setUnsavedData((prev) => {
                const updatedData = { ...prev };
                if (row.id > 0) {
                  updatedData[row.id] = rowToDelete;
                } else {
                  delete updatedData[row.id];
                }

                return updatedData;
              });
              apiRef?.current?.updateRows([rowToDelete]);
            }}
          >
            <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
          </IconButton>
        )}
      </>
    );
  };

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 120,
    renderCell: (params) => actionButtons(params),
    id: 'actions',
    className: 'actions',
  };

  const columns = useMemo(() => {
    return [
      description,
      vendorItemId,
      invoicedQtyWithUOM,
      ...(invoice?.orderId ? [poQtyWithUOM] : []),
      price,
      ...(invoice?.orderId ? [expectedItemPrice] : []),
      taxAmount,
      invoiceItemTotal,
      categoryDropdown,
      subCategoryDropdown,
      manual,
      actions,
    ];
  }, [
    invoiceItemData?.invoice?.multiFacility,
    invoiceItemData?.invoice?.facilityId,
    invoiceItemData?.invoice?.orderId,
    invoiceGLItems,
    invoiceDataLoaded,  
    hasEditAccess,
  ]);

  const facilitySubcategories = subcategories?.filter((sub) => sub.facilityId === invoice?.facilityId);
  const sortedItems = [...(invoiceItemData?.invoiceItems ?? [])]?.sort((a, b) => {
    const aMissingSub =
      a?.subcategoryId === 0 ||
      !facilitySubcategories?.some((sub) => sub.categoryId === a?.categoryId && sub.subcategoryId === a?.subcategoryId);
    const bMissingSub =
      b?.subcategoryId === 0 ||
      !facilitySubcategories?.some((sub) => sub.categoryId === b?.categoryId && sub.subcategoryId === b?.subcategoryId);

    if (aMissingSub && !bMissingSub) return -1;
    if (!aMissingSub && bMissingSub) return 1;

    if (a.price !== a.expectedItemPrice && b.price === b.expectedItemPrice) return -1;
    if (b.price !== b.expectedItemPrice && a.price === a.expectedItemPrice) return 1;

    if (a.quantity > a.expectedAmount && b.quantity <= b.expectedAmount) return -1;
    if (b.quantity > b.expectedAmount && a.quantity <= a.expectedAmount) return 1;

    if (a.expectedItemPrice > b.expectedItemPrice) return -1;
    if (b.expectedItemPrice > a.expectedItemPrice) return 1;

    return 0;
  });
  useEffect(() => {
    setRows(sortedItems || []);
  }, [invoiceItemData?.invoiceItems]);
  useEffect(() => {
    apiRef.current.updateColumns(columns);
  }, [invoiceItemData?.invoice?.multiFacility, columns, apiRef, invoiceItemData?.invoiceItems]);
  useEffect(() => {
    apiRef?.current?.scrollToIndexes({ rowIndex: 0, colIndex: 0 });
  }, [highlightMissingGls]);

  useEffect(() => {
    if (highlightItem?.form === 'electronicinvoice') {
      const rowIndex = apiRef?.current?.getRowIndexRelativeToVisibleRows(highlightItem?.id);
      if (rowIndex) apiRef?.current?.scrollToIndexes({ rowIndex, colIndex: 0 });
    }
  }, [highlightItem]);

  const handleSave = async () => {
    const response = await dispatch(patchInvoiceItems(invoice.id, Object.values(unsavedData)));
    enqueueSnackbar(response?.data?.message ? response.data.message : 'Error updating invoice.', {
      variant: response?.data?.success ? 'success' : 'error',
      persist: response?.data?.keepFormOpen,
    });
    if (response?.data?.success) {
      setUnsavedData({});
      dispatch(getInvoiceBreakdown(id, 'ItemsLog'));
    }
  };

  const handleRemoveGls = async () => {
    const response = await dispatch(deleteGlItems(id));
    if (response === 'success') {
      enqueueSnackbar('Gl items deleted successfully', { variant: 'success' });
      await dispatch(getInvoiceBreakdown(id));
    } else {
      enqueueSnackbar('failed to delete gl items', { variant: 'error' });
    }
  };

  const CustomLeftToolbar = useCallback(
    () =>
      addItemsToChat ? (
        <Box sx={{ display: 'flex', mx: 1 }}>
          <Typography variant="subtitle1">Select items to add to chat</Typography>
          <Typography
            variant="subtitle1"
            color="secondary"
            sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1 }}
            onClick={() => setAddItemsToChat(false)}
          >
            Cancel
          </Typography>
        </Box>
      ) : (
        <Grid container direction="row" justifyContent="flex-start" spacing={2} alignItems="flex-end">
          {invoiceGLItems.length === 0 && editable && (
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<Iconify icon="material-symbols:add" width="1rem" height="1rem" />}
                onClick={addInvoiceItem}
                disabled={!hasEditAccess}
              >
                Add Invoice Item
              </Button>
              <AwaitButton
                variant="outlined"
                sx={{ mr: 1, ml: 1 }}
                size="small"
                color="secondary"
                onClick={() => {
                  window.open(`${HOST_API_URL}Api/Invoices/DownloadInvoice?invoiceId=${invoice.id}`, '_blank');
                }}
                startIcon={<Iconify icon="file-icons:microsoft-excel" />}
              >
                Download Invoice
              </AwaitButton>
            </Grid>
          )}
          {hasEditAccess && invoiceGLItems?.length > 0 && (
            <Grid item>
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => setConfirmUnlockItems(true)}
                // sx={{ ml: 1, display: !hasUnsavedRows ? 'none' : 'block' }}
              >
                Unlock invoice items
              </Button>
            </Grid>
          )}
          {Object.values(unsavedData).length > 0 && (
            <>
              <Grid item>
                <Tooltip
                  title={
                    Object.values(unsavedData)?.some(
                      (item) => (!('quantity' in item) || item.quantity === 0) && item.deleteFlag !== true
                    )
                      ? 'Quantity must be greater than 0'
                      : ''
                  }
                >
                  <Button
                    disabled={Object.values(unsavedData)?.some(
                      (item) => (!('quantity' in item) || item.quantity === 0) && item.deleteFlag !== true
                    )}
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={handleSave}
                  >
                    save
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setUnsavedData({});
                    setRows(sortedItems || []);
                  }}
                >
                  Cancel
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      ),
    [unsavedData, invoiceGLItems, addItemsToChat, hasEditAccess, editable, invoice?.id]
  );

  const addInvoiceItem = () => {
    const newRow = {
      id: apiRef?.current?.getAllRowIds()?.length,
      manual: true,
    };
    apiRef?.current?.updateRows([newRow]);
    apiRef?.current?.scrollToIndexes({ rowIndex: 0 });
  };

  return (
    <>
      <Box sx={{ maxHeight: expandBottom ? '630px' : '430px' }}>
        {rows && (
          <CustomDataGrid
            key={addItemsToChat ? 'chat-selection' : 'default'}
            gridId="invoicing-order-information"
            boxSX={{ width: '100%', height: expandBottom ? '600px' : '400px' }}
            data={rows}
            isModal
            apiRef={apiRef}
            gridColumns={columns}
            isLoading={rows?.length === 0 && isLoading}
            CustomLeftToolbar={CustomLeftToolbar}
            defaultHiddenColumns={['manual']}
            checkboxSelection={!!addItemsToChat}
            rowSelectionModel={selectedChatItemIds}
            onRowSelectionModelChange={setSelectedChatItemIds}
            getRowClassName={({ id }) => {
              if (
                (highlightMissingGls &&
                  rows
                    ?.filter((item) => item.subcategoryId === 0)
                    ?.map((x) => x.id)
                    ?.includes(id)) ||
                (highlightItem?.form === 'electronicinvoice' && id === highlightItem?.id)
              )
                return 'row--highlight';
              return '';
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                marginY: '4px',
                marginRight: '8px',
                height: 38,

                '&:focus-within': { outline: `none`, border: `none` },
              },
              '& .MuiDataGrid-cell.MuiDataGrid-cell--editing, & .MuiDataGrid-cell--editable': {
                border: `solid 1px #404042`,
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
              },
              '& .MuiDataGrid-columnHeader': {
                marginRight: '8px',
              },
              '& .MuiBadge-badge': {
                display: 'none',
              },
              '& .MuiDataGrid-row.row--highlight': {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === 'light') {
                    return 'rgba(143, 223, 130, 0.3)';
                  }
                  return 'rgba(143, 223, 130, 1)';
                },
              },
            }}
          />
        )}
      </Box>
      {confirmUnlockItems && (
        <Dialog
          open
          maxWidth="lg"
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setConfirmUnlockItems(false);
          }}
        >
          <DialogTitle sx={{ pb: 2, textAlign: 'center' }}>Unlock Invoice Items</DialogTitle>
          <DialogContent sx={{ typography: 'body2' }}>
            <Box> Unlocking invoice items will remove the current GLs and recalculate</Box>

            <Box>
              them based on the invoice items in the{' '}
              <Box component="span" fontWeight="bold">
                Invoice Details
              </Box>{' '}
              section.
            </Box>
            <br />
            <Box>Do you want to continue?</Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color={'primary'}
              onClick={() => {
                setConfirmUnlockItems(false);
                handleRemoveGls();
              }}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              color={'error'}
              onClick={() => {
                setConfirmUnlockItems(false);
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
